/**
 * Service-worker registration.
 *
 * The worker itself is `public/sw.js` — plain JS, copied verbatim into `dist/`
 * by Vite and uploaded at the site root by deploy.sh's no-cache pass, so its
 * scope is the whole origin and a deploy always ships a fresh copy.
 *
 * It exists for three reasons, in this order:
 *  1. Chromium will not treat an app as installable without a worker that has
 *     a `fetch` handler.
 *  2. Offline tolerance for the shell itself — a cached `index.html` means a
 *     launched-from-the-homescreen app on a dead connection shows the shell
 *     rather than the browser's dinosaur.
 *  3. For a tenant whose modules are same-origin, offline tolerance for the
 *     APP: the module bundles and the resolve answer, so the launch lands on a
 *     working screen rather than empty chrome (DEV-1021).
 *
 * The tenant's policy travels in the worker's own SCRIPT URL. A fetch handler
 * has to decide synchronously whether to respond, so it cannot wait on a cache
 * read or a postMessage; the script URL is the one thing a worker knows before
 * its first event. Hence `/sw.js?tenant=<slug>&resolve=<url>[&offline=1]`.
 */
import { watchForUpdates } from "@/pwa/appUpdate";
import { makeModuleUpdateCheck } from "@/pwa/moduleVersion";
import { isFieldApp } from "@/tenant/fieldApp";
import type { ResolvedMfe, ResolveTenantAppResponse } from "@/tenant/types";

/**
 * The exact URL the shell fetched to resolve this tenant. The worker matches
 * this string, and only this string, so it must be built the same way
 * `fetchTenantApp` builds it (`tenant/resolve.ts`).
 */
export function resolveUrlFor(host: string, apiBase: string): string {
  return `${apiBase}/tenant-apps/resolve?host=${encodeURIComponent(host)}`;
}

/**
 * Whether this tenant's app should be made to work offline.
 *
 * ── This used to be derived, and the derivation could never be true ──────────
 *
 * The original rule (DEV-1021) was "every module in the import map is
 * same-origin", on the premise that *"the worker can only cache modules it is
 * allowed to read and re-serve, which means same-origin `/mfe/*`"*, and it
 * answered false for every tenant. DEV-1110 measured the live map for
 * `boer.metaship.ai`: **23 entries — 17 on cdn.jsdelivr.net, 1 on
 * cdnjs.cloudflare.com, 1 on default.metaship.ai, 2 module distributions, 2
 * relative.** Same-origin is not a state that map can reach without re-hosting
 * the whole vendor set, so the layer would have stayed dormant indefinitely.
 *
 * The premise is also not quite right, and the worker already knew it: it
 * precaches tenant BRAND assets from `cdn.metaship.ai`, noting they serve
 * `access-control-allow-origin: *` and so are *"real and inspectable, never
 * opaque"*. The same is true of every origin in that map — checked live,
 * jsdelivr, cdnjs and the module distributions all send
 * `access-control-allow-origin: *`, and the module bundles are `immutable`
 * besides. A CORS-readable response is as cacheable as a same-origin one.
 *
 * So the question this answers is now INTENT — is this app used away from
 * signal — and the worker decides per URL what it can actually cache, which is
 * a property it can test rather than one the shell can only guess at. An entry
 * that will not cache is skipped and falls through to the network, exactly as
 * it does today.
 */
export function shouldRunOffline(modules: Pick<ResolvedMfe, "pathPrefix">[]): boolean {
  return isFieldApp(modules);
}

/** `/sw.js` plus the tenant policy the worker reads out of its own script URL. */
export function serviceWorkerUrl(params: { tenant: string; resolveUrl: string; offline: boolean }): string {
  const query = new URLSearchParams({ tenant: params.tenant, resolve: params.resolveUrl });
  if (params.offline) query.set("offline", "1");
  return `/sw.js?${query.toString()}`;
}

export async function registerServiceWorker(options: {
  tenant: Pick<ResolveTenantAppResponse, "slug" | "importMap" | "modules">;
  host: string;
  apiBase: string;
}): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  // A worker registered from `vite dev` would cache the dev server's module
  // graph and shadow every subsequent edit.
  if (!import.meta.env.PROD) return null;
  try {
    const url = serviceWorkerUrl({
      tenant: options.tenant.slug || "",
      resolveUrl: resolveUrlFor(options.host, options.apiBase),
      offline: shouldRunOffline(options.tenant.modules ?? []),
    });
    // `updateViaCache: "none"` so the browser's HTTP cache can never hand back
    // a stale worker and pin the origin to a deleted build's asset names.
    const registration = await navigator.serviceWorker.register(url, { scope: "/", updateViaCache: "none" });
    // The worker parks an update rather than taking over; this is what notices
    // and offers the person the moment (pwa/appUpdate.ts).
    //
    // The second argument is the OTHER kind of update: a module deploy leaves
    // `sw.js` byte-identical, so no worker ever parks and the banner could not
    // fire for the commonest kind of release. This is the only place that
    // holds both the map the page booted with and the way to re-resolve it.
    watchForUpdates(
      registration,
      makeModuleUpdateCheck(options.tenant.importMap?.imports ?? [], options.host, options.apiBase),
    );
    return registration;
  } catch {
    // A blocked or unsupported registration costs the install prompt, nothing else.
    return null;
  }
}
